import { Component, computed, input, output, signal } from '@angular/core';
import { type SidebarBrand, type SidebarBrandAction, type SidebarNavItem } from './sidebar.model';

@Component({
  selector: 'lib-sidebar',
  standalone: true,
  host: {
    '[class]': 'hostClasses()',
  },
  template: `
    <!-- Brand -->
    <div class="msa-sidebar__brand" (click)="toggleBrandMenu($event)">
      <div class="msa-sidebar__avatar" [style.background]="brand().avatarBg ?? 'rgba(52, 211, 153, 0.2)'">
        @if (brand().imageUrl) {
          <img
            [src]="brand().imageUrl"
            [alt]="brand().title"
            class="msa-sidebar__avatar-img"
            loading="lazy"
            (error)="avatarImageError.emit()"
          />
        } @else {
          <span>{{ brand().icon }}</span>
        }
      </div>
      <div class="msa-sidebar__brand-text">
        <p class="msa-sidebar__brand-title">{{ brand().title }}</p>
        @if (brand().subtitle) {
          <p class="msa-sidebar__brand-subtitle">{{ brand().subtitle }}</p>
        }
      </div>
      <svg class="msa-sidebar__chevron" [class.msa-sidebar__chevron--open]="brandMenuOpen()" width="12" height="12" viewBox="0 0 20 20" fill="currentColor">
        <path d="M5 8l5 5 5-5H5z"></path>
      </svg>
    </div>

    <!-- Brand dropdown -->
    @if (brandMenuOpen() && brand().actions?.length) {
      <div class="msa-sidebar__brand-menu">
        @for (action of brand().actions; track action.id) {
          <button
            class="msa-sidebar__brand-menu-item"
            (click)="onBrandAction($event, action)"
          >
            @if (action.icon) {
              <span class="msa-sidebar__brand-menu-icon">{{ action.icon }}</span>
            }
            <span>{{ action.label }}</span>
          </button>
        }
      </div>
    }

    <!-- Navigation -->
    <nav class="msa-sidebar__nav">
      @for (item of items(); track item.id) {
        <a
          class="msa-sidebar__link"
          [class.msa-sidebar__link--active]="item.id === activeId()"
          [href]="item.href ?? '#'"
          (click)="onItemClick($event, item)"
        >
          <span class="msa-sidebar__icon">{{ item.icon }}</span>
          <span class="msa-sidebar__label">{{ item.label }}</span>
          @if (item.badge) {
            <span class="msa-sidebar__badge">{{ item.badge }}</span>
          }
          @if (item.hasSubmenu) {
            <svg class="msa-sidebar__submenu-icon" width="12" height="12" viewBox="0 0 20 20" fill="currentColor">
              <path d="M5 8l5 5 5-5H5z"></path>
            </svg>
          }
        </a>
      }
    </nav>

    <!-- Footer -->
    <div class="msa-sidebar__footer">
      <ng-content select="[sidebar-footer]" />
    </div>
  `,
  styles: [`
    /* ═══════════════════════════════════════════════
       MSA Sidebar Compound
       ═══════════════════════════════════════════════
       Vertical navigation sidebar with brand, nav items,
       and footer slot. All styles on :host / :host(.class).
       ═══════════════════════════════════════════════ */

    :host {
      display: flex;
      flex-direction: column;
      width: 216px;
      background: #0b1c33;
      color: #fff;
      flex-shrink: 0;
      font-family: inherit;
      box-sizing: border-box;
    }

    /* ──────────────────────────────────────
       Brand
       ────────────────────────────────────── */
    .msa-sidebar__brand {
      display: flex;
      align-items: center;
      gap: 8px;
      padding: 20px;
      border-bottom: 1px solid rgba(255, 255, 255, 0.1);
      cursor: pointer;
      transition: background 0.15s ease;
    }

    .msa-sidebar__brand:hover {
      background: rgba(255, 255, 255, 0.05);
    }

    .msa-sidebar__avatar {
      width: 32px;
      height: 32px;
      border-radius: 50%;
      display: flex;
      align-items: center;
      justify-content: center;
      font-size: 16px;
      flex-shrink: 0;
      overflow: hidden;
    }

    .msa-sidebar__avatar-img {
      width: 100%;
      height: 100%;
      border-radius: 50%;
      object-fit: cover;
    }

    .msa-sidebar__brand-text {
      flex: 1;
      min-width: 0;
      line-height: 1.3;
    }

    .msa-sidebar__brand-title {
      font-size: 13px;
      font-weight: 600;
      margin: 0;
      overflow: hidden;
      text-overflow: ellipsis;
      white-space: nowrap;
    }

    .msa-sidebar__brand-subtitle {
      font-size: 12.5px;
      color: rgba(255, 255, 255, 0.7);
      margin: -2px 0 0;
      overflow: hidden;
      text-overflow: ellipsis;
      white-space: nowrap;
    }

    .msa-sidebar__chevron {
      margin-left: auto;
      flex-shrink: 0;
      color: rgba(255, 255, 255, 0.5);
      transition: transform 0.2s ease;
    }

    .msa-sidebar__chevron--open {
      transform: rotate(180deg);
    }

    /* ──────────────────────────────────────
       Brand dropdown menu
       ────────────────────────────────────── */
    .msa-sidebar__brand-menu {
      background: #112240;
      border-bottom: 1px solid rgba(255, 255, 255, 0.1);
      padding: 4px 0;
    }

    .msa-sidebar__brand-menu-item {
      display: flex;
      align-items: center;
      gap: 10px;
      width: 100%;
      padding: 10px 20px;
      border: none;
      background: none;
      color: rgba(255, 255, 255, 0.7);
      font-size: 13px;
      font-family: inherit;
      cursor: pointer;
      text-align: left;
      transition: all 0.15s ease;
    }

    .msa-sidebar__brand-menu-item:hover {
      background: rgba(255, 255, 255, 0.05);
      color: #fff;
    }

    .msa-sidebar__brand-menu-icon {
      width: 16px;
      text-align: center;
      flex-shrink: 0;
    }

    /* ──────────────────────────────────────
       Navigation
       ────────────────────────────────────── */
    .msa-sidebar__nav {
      flex: 1;
      padding: 12px;
      display: flex;
      flex-direction: column;
      gap: 2px;
    }

    .msa-sidebar__link {
      display: flex;
      align-items: center;
      gap: 12px;
      padding: 10px 12px;
      border-radius: 8px;
      font-size: 13px;
      color: rgba(255, 255, 255, 0.7);
      text-decoration: none;
      transition: all 0.15s ease;
      min-width: 0;
    }

    .msa-sidebar__link:hover {
      background: rgba(255, 255, 255, 0.05);
      color: #fff;
    }

    .msa-sidebar__link--active {
      background: #2563eb;
      color: #fff;
      font-weight: 500;
    }

    .msa-sidebar__icon {
      width: 16px;
      text-align: center;
      flex-shrink: 0;
    }

    .msa-sidebar__label {
      flex: 1;
      min-width: 0;
      overflow: hidden;
      text-overflow: ellipsis;
      white-space: nowrap;
    }

    .msa-sidebar__badge {
      margin-left: auto;
      background: #ef4444;
      color: #fff;
      font-size: 10px;
      font-weight: 600;
      min-width: 18px;
      height: 18px;
      display: flex;
      align-items: center;
      justify-content: center;
      border-radius: 50%;
      flex-shrink: 0;
    }

    .msa-sidebar__submenu-icon {
      margin-left: auto;
      opacity: 0.6;
      flex-shrink: 0;
    }

    /* ──────────────────────────────────────
       Footer
       ────────────────────────────────────── */
    .msa-sidebar__footer {
      padding: 0 12px 16px;
    }

    /* ──────────────────────────────────────
       Mobile drawer — < 1024px
       Hidden off-canvas by default, slides in
       as a fixed overlay when .--mobile-open.
       Desktop keeps the static 216px sidebar.
       ────────────────────────────────────── */
    @media (max-width: 1023.98px) {
      :host {
        position: fixed;
        top: 0;
        bottom: 0;
        left: 0;
        width: 260px;
        z-index: 60;
        transform: translateX(-100%);
        transition: transform 0.25s ease;
        box-shadow: 0 0 32px rgba(0, 0, 0, 0.35);
        will-change: transform;
      }

      :host(.msa-sidebar--mobile-open) {
        transform: translateX(0);
      }
    }

    @media (prefers-reduced-motion: reduce) {
      :host {
        transition: none;
      }
    }
  `]
})
export class SidebarComponent {
  /** Brand section configuration */
  readonly brand = input.required<SidebarBrand>();

  /** Navigation items */
  readonly items = input.required<SidebarNavItem[]>();

  /** Active navigation item ID */
  readonly activeId = input<string>('');

  /** Emitted when a nav item is clicked */
  readonly itemClicked = output<SidebarNavItem>();

  /** Emitted when a brand action is clicked (e.g. logout) */
  readonly brandActionClicked = output<SidebarBrandAction>();

  /** Emitted when the brand avatar image fails to load */
  readonly avatarImageError = output<void>();

  /**
   * When true, renders the sidebar as an open drawer overlay on small screens
   * (< 1024px). On desktop it has no effect: the sidebar stays static/fixed 216px.
   */
  readonly mobileOpen = input<boolean>(false);

  /** Brand dropdown open state */
  readonly brandMenuOpen = signal(false);

  readonly hostClasses = computed(() => ({
    'msa-sidebar': true,
    'msa-sidebar--mobile-open': this.mobileOpen(),
  }));

  toggleBrandMenu(event: MouseEvent): void {
    event.stopPropagation();
    this.brandMenuOpen.update(v => !v);
  }

  onBrandAction(event: MouseEvent, action: SidebarBrandAction): void {
    event.stopPropagation();
    this.brandMenuOpen.set(false);
    this.brandActionClicked.emit(action);
  }

  onItemClick(event: MouseEvent, item: SidebarNavItem): void {
    event.preventDefault();
    this.itemClicked.emit(item);
  }
}
